import { cache, imageLoader, metaData } from '@cornerstonejs/core';
/** Modalities whose stored window is never trusted. PET vendors routinely write
 *  the full activity range, and the only good PET window is SUV- or data-driven. */
const DATA_WINDOWED_MODALITIES = new Set(['PT']);
/** True when the image's display window should come from its pixels, not its header. */
export function isDataWindowedModality(modality) {
    return DATA_WINDOWED_MODALITIES.has((modality ?? '').trim().toUpperCase());
}
/**
 * Whether a stored window (0028,1050/1051) is worth applying as-is.
 *
 * Rejected: PET (see above); a missing or non-positive width; and a window at
 * least as wide as everything the stored bits can represent (65535 on 16-bit
 * data), which is a "no window" sentinel some consoles write rather than a
 * choice — it flattens the image to one grey. Everything else is trusted,
 * including wide-but-plausible DX/CR windows, so no working image changes.
 */
export function isUsableStoredWindow(input) {
    if (isDataWindowedModality(input.modality))
        return false;
    const wc = input.windowCenter;
    const ww = input.windowWidth;
    if (typeof wc !== 'number' || !Number.isFinite(wc))
        return false;
    if (typeof ww !== 'number' || !Number.isFinite(ww) || ww <= 0)
        return false;
    const bits = input.bitsStored;
    if (typeof bits === 'number' && bits > 0 && bits <= 32) {
        const slope = Math.abs(input.rescaleSlope ?? 1) || 1;
        const representable = (2 ** bits - 1) * slope;
        if (ww >= representable)
            return false;
    }
    return true;
}
const DEFAULT_MAX_SAMPLES = 200_000;
/**
 * A stride sample of `pixels` with the rescale applied: at most `maxSamples`
 * values, NaN read as 0. An odd hop never aliases with an even row width (a
 * hop equal to the width would sample one column over and over); the +1/+2
 * keeps it odd and > 1.
 */
function sampleRescaled(pixels, slope, intercept, maxSamples) {
    const n = pixels.length;
    const base = Math.max(1, Math.floor(n / Math.max(1, maxSamples)));
    const stride = base === 1 ? 1 : base + (base % 2 === 0 ? 1 : 2);
    const sample = new Float64Array(Math.ceil(n / stride));
    const s = Number.isFinite(slope) && slope !== 0 ? slope : 1;
    const b = Number.isFinite(intercept) ? intercept : 0;
    let k = 0;
    for (let i = 0; i < n; i += stride) {
        const v = pixels[i];
        sample[k++] = (Number.isFinite(v) ? v : 0) * s + b;
    }
    return sample.subarray(0, k);
}
/**
 * A window in rescaled (modality LUT applied) units covering the central bulk
 * of the pixel distribution. Pure; O(n) stride sample plus a sort of at most
 * `maxSamples` values. Returns null when the pixels carry no spread.
 */
export function percentileWindow(pixels, slope = 1, intercept = 0, options = {}) {
    if (pixels.length === 0)
        return null;
    const lowPct = options.lowPercentile ?? 0.5;
    const highPct = options.highPercentile ?? 99.5;
    const sorted = sampleRescaled(pixels, slope, intercept, options.maxSamples ?? DEFAULT_MAX_SAMPLES).sort();
    const k = sorted.length;
    const at = (pct) => {
        const idx = Math.min(k - 1, Math.max(0, Math.round(((k - 1) * pct) / 100)));
        return sorted[idx];
    };
    let lower = at(lowPct);
    let upper = at(highPct);
    if (lower > upper)
        [lower, upper] = [upper, lower];
    if (upper - lower <= 0) {
        // Flat sample (all background): fall back to the full span so something shows.
        lower = sorted[0];
        upper = sorted[k - 1];
        if (upper - lower <= 0)
            return null;
    }
    return { lower, upper };
}
/**
 * The rescale still owed to an image's pixel data: none once the loader has
 * pre-scaled it (Cornerstone does so for CT and PET, so getPixelData() is
 * already HU / Bq/ml — applying the header's slope again turned a CT's
 * −1000..2541 HU into −1340..−136), else the header's slope and intercept.
 */
function pendingRescale(image) {
    if (image.preScale?.scaled)
        return { slope: 1, intercept: 0 };
    return { slope: image.slope ?? 1, intercept: image.intercept ?? 0 };
}
function cachedPixels(imageId) {
    try {
        const image = cache.getImage(imageId);
        if (!image || image.color)
            return null;
        const pixels = image.getPixelData?.();
        if (!pixels || pixels.length === 0)
            return null;
        return { image, pixels };
    }
    catch {
        return null;
    }
}
/**
 * A percentile window for an image already in the Cornerstone cache, or null
 * when it is not loaded, is colour, or has no spread.
 */
export function dataWindowForCachedImage(imageId) {
    const cached = cachedPixels(imageId);
    if (!cached)
        return null;
    const { slope, intercept } = pendingRescale(cached.image);
    return percentileWindow(cached.pixels, slope, intercept);
}
/**
 * A percentile window over several cached images pooled together — a few
 * interior frames stand in for the volume, which no single slice does well
 * (one slice through the hottest organ of a PET windows the rest of the
 * subject away). Each image's own pending rescale is applied before pooling.
 * Null when none of them is usable.
 */
export function dataWindowForCachedImages(imageIds, options = {}) {
    const total = options.maxSamples ?? DEFAULT_MAX_SAMPLES;
    const perImage = Math.max(1, Math.floor(total / Math.max(1, imageIds.length)));
    const parts = [];
    for (const id of imageIds) {
        const cached = cachedPixels(id);
        if (!cached)
            continue;
        const { slope, intercept } = pendingRescale(cached.image);
        parts.push(sampleRescaled(cached.pixels, slope, intercept, perImage));
    }
    if (parts.length === 0)
        return null;
    const pooled = new Float64Array(parts.reduce((n, p) => n + p.length, 0));
    let offset = 0;
    for (const part of parts) {
        pooled.set(part, offset);
        offset += part.length;
    }
    return percentileWindow(pooled, 1, 0, { ...options, maxSamples: pooled.length });
}
/** How many interior frames of a stack stand in for its volume. */
const SAMPLE_FRAMES = 5;
/**
 * Evenly spaced interior indices of a stack of `length` frames — never the
 * first or last, which on a reconstructed volume are padding (a Mediso PET
 * stores an all-zero first frame) or air.
 */
export function sampleFrameIndices(length, count = SAMPLE_FRAMES) {
    if (length < 3)
        return [];
    const picked = new Set();
    for (let i = 1; i <= count; i++) {
        picked.add(Math.min(length - 2, Math.max(1, Math.round(((length - 1) * i) / (count + 1)))));
    }
    return [...picked].sort((a, b) => a - b);
}
/**
 * The modality the loaded object's own header declares, upper-cased, else the
 * caller's fallback. The study endpoint hands every series the STUDY's
 * modality (`series` has no modality column), so on a PET/CT the CT series
 * says "PT" when the PET object arrived first and the PET series says "CT"
 * otherwise. Anything that keys on the modality of what is on screen — the
 * MPR gate, the PET window — must ask the header once it is loaded.
 */
export function headerModality(imageId, fallback) {
    if (imageId) {
        try {
            const series = metaData.get('generalSeriesModule', imageId);
            if (typeof series?.modality === 'string' && series.modality.trim()) {
                return series.modality.trim().toUpperCase();
            }
        }
        catch {
            // A provider threw: fall through to the caller's value.
        }
    }
    const trimmed = fallback?.trim();
    return trimmed ? trimmed.toUpperCase() : null;
}
/**
 * Whether a freshly opened stack needs a window computed from its pixels:
 * PET always, and any object whose stored window the metadata provider
 * refused (it answers an EMPTY voiLutModule for a sentinel such as the
 * CT 0 / 65535, so the loader fell back to the opening frame's min/max —
 * which, on an edge slice of air, saturates everything in the body).
 */
export function needsDataWindow(imageId, modality) {
    if (isDataWindowedModality(modality))
        return true;
    try {
        const voi = metaData.get('voiLutModule', imageId);
        return voi != null && typeof voi === 'object' && Object.keys(voi).length === 0;
    }
    catch {
        return false;
    }
}
function sameRange(a, b) {
    return !!a && !!b && a.lower === b.lower && a.upper === b.upper;
}
/**
 * Window a freshly loaded stack from its pixels when its header window cannot
 * be trusted (see needsDataWindow). The opening frame is windowed at once from
 * the cache; a few interior frames are then loaded and the window recomputed
 * from all of them pooled, because the opening frame of a volume is an edge
 * slice — air on a CT, nothing at all on a PET — whose percentiles say little
 * about the subject. The second pass is skipped when `isCurrent` reports the
 * pane has moved on, or when the reader has already set their own window.
 *
 * Called after setStack, before the first render; a saved presentation (the
 * reader's own window) must be applied INSTEAD of this, which is the caller's
 * decision. Resolves to the last range applied, or null when nothing changed.
 */
export async function applyDataWindow(viewport, imageIds, openingIndex, fallbackModality, isCurrent = () => true) {
    const openingId = imageIds[openingIndex];
    if (!openingId)
        return null;
    if (!needsDataWindow(openingId, headerModality(openingId, fallbackModality)))
        return null;
    const apply = (range) => {
        try {
            viewport.setProperties({ voiRange: range });
            return true;
        }
        catch {
            return false;
        }
    };
    let applied = null;
    const opening = dataWindowForCachedImage(openingId);
    if (opening && apply(opening))
        applied = opening;
    const sampleIds = sampleFrameIndices(imageIds.length)
        .map((i) => imageIds[i])
        .filter((id) => !!id);
    if (sampleIds.length === 0)
        return applied;
    const before = applied ?? viewport.getProperties?.()?.voiRange;
    const loads = await Promise.allSettled(sampleIds.map((id) => imageLoader.loadAndCacheImage(id)));
    const loaded = sampleIds.filter((_, i) => loads[i].status === 'fulfilled');
    if (loaded.length === 0 || !isCurrent())
        return applied;
    // The reader beat the sample frames to it: their window wins.
    const now = viewport.getProperties?.()?.voiRange;
    if (before && now && !sameRange(before, now))
        return applied;
    const sample = dataWindowForCachedImages(loaded);
    if (!sample || !apply(sample))
        return applied;
    try {
        viewport.render?.();
    }
    catch {
        // Rendering is the viewport's problem; the window is set.
    }
    return sample;
}
